import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { buildRequestsFilter } from '@/modules/service-desk/shared/application-api';
import { FULFILMENT_STATUS_OPTIONS } from '@/modules/service-desk/shared/application-utils';
import { buildCsv, asExcelText, downloadCsv, exportDateStamp, formatRequestedOn } from '@/lib/csv';
import { useRequestFilters } from '@/modules/service-desk/shared/use-request-filters';
import { fetchAllPages } from '@/lib/fetch-all-pages';
import { mergeWhere } from '@/lib/table-query';
import {
  ApplicationsTable,
  useApplicationsTable,
} from '@/modules/service-desk/shared/components/applications-table';
import { ExportCsvButton } from '@/modules/service-desk/shared/components/export-csv-button';
import { businessCardApi } from '../../services/business-card.service';
import {
  useBusinessCardApplications,
  useChangeBusinessCardStatus,
} from '../../hooks/use-business-card';
import { createBusinessCardRequestColumns } from '../business-card-columns/business-card-columns';

const CSV_HEADERS = ['Requested On', 'Name', 'Designation', 'Mobile Number', 'Email', 'Status'];

export const BusinessCardRequests = () => {
  const { t } = useTranslation();
  const canUpdateStatus = useHasPermission(HRM_PERMISSIONS.BUSINESS_CARD_APPLICATION.UPDATE_STATUS);
  const { filters, departments, departmentCondition, isScopeLoading } = useRequestFilters();
  const table = useApplicationsTable();
  const { resetPage } = table;

  useEffect(() => resetPage(), [filters, resetPage]);

  const filter = mergeWhere(
    buildRequestsFilter(filters, 'Name', departmentCondition),
    table.columnWhere
  );
  const { data, isLoading, error } = useBusinessCardApplications({
    pageNo: table.pagination.pageIndex + 1,
    pageSize: table.pagination.pageSize,
    filter,
    order: table.order,
  });
  const {
    mutateAsync: changeStatus,
    isPending: isSavingStatus,
    variables: savingStatus,
  } = useChangeBusinessCardStatus();
  const savingId = isSavingStatus ? savingStatus?.itemId : undefined;

  const columns = useMemo(
    () =>
      createBusinessCardRequestColumns({
        t,
        departments,
        canUpdateStatus,
        savingId,
        onChangeStatus: (application, status) =>
          changeStatus({ itemId: application.ItemId, status }),
      }),
    [t, departments, canUpdateStatus, savingId, changeStatus]
  );

  /** Legacy CSV: every row matching the current filters, not just the visible page. */
  const exportCsv = async () => {
    const all = await fetchAllPages((pageNo, pageSize) =>
      businessCardApi.list({ pageNo, pageSize, filter, order: table.order })
    );
    const rows = all.map((item) => ({
      'Requested On': formatRequestedOn(item.AppliedDateUtc, item.Timezone),
      Name: item.Name,
      Designation: item.Designation,
      'Mobile Number': asExcelText(item.MobileNumber),
      Email: item.Email,
      Status:
        t(FULFILMENT_STATUS_OPTIONS.find((o) => o.value === item.Status)?.labelKey ?? '') ||
        item.Status,
    }));
    const date = exportDateStamp();
    downloadCsv(`Business Card Requests_${date}`, buildCsv(CSV_HEADERS, rows));
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Name, department and status are filtered from their columns. On a phone the download
          sits on the cards' line, beside the filter button. */}
      <div className="flex justify-end max-md:hidden">
        <ExportCsvButton onExport={exportCsv} className="w-auto" />
      </div>
      <ApplicationsTable
        columns={columns}
        result={data}
        isLoading={isLoading || isScopeLoading}
        error={error}
        table={table}
        mobileActions={<ExportCsvButton onExport={exportCsv} />}
      />
    </div>
  );
};
